import { useMemo, useState } from "react";
import dayjs from "dayjs";
import { getCookie, setCookie } from "../utils/cookie";
import { isForkPage, redirectToUrl } from "../utils/url/urlUtils";
import { IconButton } from "@mui/material";
import { Icon } from "../common/components/icon/Icon";
import { IconType } from "../common/components/icon/IconType";
import { SimpleSelect } from "../common/components/Select/SimpleSelect";
import { resolveApiUrl } from "../utils/url/urlResolver";
import { useDispatch, useSelector } from "react-redux";
import { updateVisibility } from "./actions";
import { selectRegionaBannerVisibility } from "./selectors";
import { useTranslation } from "react-i18next";
import { Button } from "~/common/components/Button/Button";

const dropdownOptions = window.SCHA.regionSelectionBanner.dropdownUrls;
const defaultDropdownOption = window.SCHA.regionSelectionBanner.dropdownUrls[0];

export const RegionSelectionBanner = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const isOpen = useSelector(selectRegionaBannerVisibility);
  const [dropdownOption, setDropdownOption] = useState(defaultDropdownOption);

  const handleConfirmClick = () => {
    dispatch(updateVisibility(false));
    if (dropdownOption.value == "1") {
      redirectToUrl(dropdownOption.url);
    } else {
      fetch(createUrlFromInputValues(dropdownOption.url), {
        method: "GET",
        credentials: "same-origin", // include, omit
        mode: "cors", // no-cors, same-origin
        referrer: "no-referrer", // client
        headers: {
          dataType: "json",
          "content-type": "application/json",
        },
      }).then((response) => {
        if (!response.ok) {
          throw alert("Error status: " + response.status + ", please try again");
        } else {
          response.json().then((data) => redirectToUrl(data));
        }
      });
    }
  };

  function createUrlFromInputValues(countryIsoCode: string): string {
    const params = new URLSearchParams();
    params.set("countryIsoCode", countryIsoCode);
    params.set("currentUri", window.location.href.replace(window.location.origin, ""));

    return resolveApiUrl(`/regionSelectionBanner/getRegionSite?${params}`);
  }

  const handleCloseClick = () => {
    dispatch(updateVisibility(false));
    setCookie({
      name: COOKIE,
      value: "closed",
      expireDate: dayjs().add(1, "w").toDate(), // expires in 1 week
    });
  };

  const options = useMemo(() => {
    return dropdownOptions.map((option) => {
      return {
        ...option,
        label: t(option.label),
      };
    });
  }, [dropdownOptions]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="scha__region-selection-banner__wrapper scha__screen-only">
      <div className="scha__region-selection-banner container">
        <div className={"scha__region-selection-banner__message"}>{t("regionSelectionBanner.message")}</div>

        <div className={"scha__region-selection-banner__select"}>
          <SimpleSelect
            value={dropdownOption.value}
            values={options}
            onChange={(e) => setDropdownOption(dropdownOptions[+e.target.value])}
            label={t("regionSelectionBanner.region")}
            id={"region_selection_dropdown"}
            fullWidth
          />
        </div>
        <div className="scha__region-selection-banner__actions">
          <Button
            size={"medium"}
            className={"scha__region-selection-banner__confirm-button"}
            onClick={handleConfirmClick}
          >
            {t("regionSelectionBanner.apply")}
          </Button>

          <div className={"scha__region-selection-banner__close-button"}>
            <IconButton color={"primary"} onClick={handleCloseClick}>
              <Icon type={IconType.Close} />
            </IconButton>
          </div>
        </div>
      </div>
    </div>
  );
};

const COOKIE = "cache.region";

export const showRegionBanner = () => {
  return window.SCHA.regionSelectionBanner.showRedirectBanner && !getCookie(COOKIE) && !isForkPage();
};

export function createRegionSelectionBanner() {
  if (!showRegionBanner()) {
    return null;
  }
  return <RegionSelectionBanner />;
}
